/**
 * Creates an object composed of the `object` properties `predicate` returns
 * truthy for. The predicate is invoked with two arguments:  key, value).
 */
export const pickBy = (
  object: Record<string, unknown> | null | undefined,
  predicate: (key: string, value: unknown) => boolean
) => {
  const result: Record<string, unknown> = {};

  if (object == null || typeof object === "undefined") {
    return {};
  }

  Object.keys(object).forEach((key) => {
    if (predicate(key, object[key])) {
      result[key] = object[key];
    }
  });

  return result;
};

export const pickKeysWithValues = (
  object: Record<string, unknown> | null | undefined,
  keys?: string[]
) => {
  const _keys = keys || Object.keys(object || {});
  return pickBy(object, (key, value) => _keys.includes(key) && typeof value !== "undefined");
};

// oxlint-disable-next-line typescript/no-explicit-any
export function pickKeys<T extends Record<string, any>, K extends keyof T>(obj: T, keys: K[]) {
  return keys.reduce(
    (acc, key) => {
      acc[key] = obj[key];
      return acc;
    },
    {} as Pick<T, K>
  );
}

// oxlint-disable-next-line typescript/no-explicit-any
export function omitKeys<T extends Record<string, any>, K extends keyof T>(obj: T, keys: K[]) {
  return Object.keys(obj).reduce(
    (acc, key) => {
      if (!keys.includes(key as K)) {
        // oxlint-disable-next-line typescript/no-explicit-any
        (acc as any)[key] = obj[key];
      }
      return acc;
    },
    {} as Omit<T, K>
  );
}

// oxlint-disable-next-line typescript/no-explicit-any
export function pickKeysWithRest<T extends Record<string, any>, K extends keyof T>(
  obj: T,
  keys: K[]
) {
  const picked = {} as Pick<T, K>;
  const rest = Object.keys(obj).reduce(
    (acc, key) => {
      if (!keys.includes(key as K)) {
        // oxlint-disable-next-line typescript/no-explicit-any
        (acc as any)[key] = obj[key];
      } else {
        picked[key as K] = obj[key];
      }

      return acc;
    },
    {} as Omit<T, K>
  );

  return [picked, rest] as const;
}
